import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import type { KeyValuePair } from '@invision/core';
import { useAuthenticationFlow } from '../contexts';
import { useCloseSessionMutation } from '../apis/useCloseSessionMutation';
import { AuthenticationStorage, clearAuthenticationApiSession } from '../utils';
import { AUTHENTICATION_ROUTES } from '../constants';
import type { SecurityCloseSessionRequest } from '../types';

interface LogoutOptions {
  /** Custom redirect URL after logout (optional) */
  redirectUrl?: string;
  /** Whether to perform complete cleanup including username (default: true) */
  completeCleanup?: boolean;
  /** Report a failed CloseSession after local cleanup to a coordinating owner. */
  propagateError?: boolean;
  /**
   * Full preference set to persist while ending the session. CloseSession
   * replaces the stored preferences with this list, so callers must send
   * the complete set (existing prefs plus any LAST-URL upsert). When
   * omitted or empty, Preferences is left off the request — an empty
   * array would wipe every stored preference.
   */
  preferences?: KeyValuePair<string>[];
}

interface CloseSessionMutationParams {
  request: SecurityCloseSessionRequest;
  options?: LogoutOptions;
}

interface SSOMethods {
  /** Placeholder for SSO logout coordination */
  performSSOLogout?: () => Promise<void>;
}

/**
 * Abstract method for performing authentication storage cleanup
 */
function performAuthenticationCleanup(completeCleanup: boolean = true) {
  if (completeCleanup) {
    // Clear everything including username and environment preferences
    AuthenticationStorage.clearAllIncludingUsername();
  } else {
    // Preserve username and environment for better UX
    AuthenticationStorage.clearAll();
  }

  // Drop the cached in-memory session so it cannot leak into the next
  // (unauthenticated) request after the user returns to the login page.
  clearAuthenticationApiSession();
}

/**
 * useLogout Hook
 *
 * Provides environment-aware logout functionality with comprehensive cleanup.
 * Handles both regular logout and SSO logout coordination (placeholder).
 *
 * Features:
 * - Environment awareness
 * - Configurable post-logout redirect
 * - Complete session cleanup
 * - SSO logout coordination (placeholder)
 * - Error handling with fallback cleanup
 */
export function useLogout() {
  const history = useHistory();
  const { sessionId, systemId, clearState } = useAuthenticationFlow();
  const closeSessionMutation = useCloseSessionMutation();

  /**
   * Main logout function with environment awareness
   */
  const logout = useCallback(
    async (options?: LogoutOptions) => {
      try {
        // Ensure we have session data before attempting logout
        if (!sessionId || !systemId) {
          // Still perform cleanup even without session
          performDirectCleanup(options);
          return;
        }

        // Session headers are added by the service layer. Include Preferences
        // only when there is a non-empty set to persist (e.g. LAST-URL).
        // CloseSession replaces the stored set, so [] would wipe language,
        // sales channel, and every other user preference.
        const preferences = options?.preferences;
        const logoutParams: CloseSessionMutationParams = {
          request:
            preferences && preferences.length > 0
              ? { Preferences: preferences }
              : {},
          options
        };

        // Perform API logout with comprehensive cleanup. Awaited so the
        // mutation's `onSettled` cleanup (clearing the stored session) completes
        // before the caller navigates away — otherwise the login route's
        // SessionGuard could still see a live session and bounce the user back
        // into an application.
        await closeSessionMutation.mutateAsync(logoutParams);
      } catch (error) {
        // Perform cleanup even if logout fails (security best practice)
        performDirectCleanup(options);

        if (options?.propagateError) throw error;
      }
    },
    [sessionId, systemId, closeSessionMutation]
  );

  /**
   * SSO Logout Coordination (Placeholder)
   *
   * Future implementation for coordinating logout with external Identity Providers.
   * This is where we would integrate with SAML, OIDC, or other SSO providers.
   */
  const logoutWithSSO = useCallback(
    async (ssoMethods: SSOMethods, options?: LogoutOptions) => {
      try {
        // SSO logout coordination
        if (ssoMethods.performSSOLogout) {
          // SSO logout coordination will be implemented when SSO integration is available
          // This will ensure proper logout across all SSO-enabled applications
        }

        // Perform regular logout after SSO coordination
        await logout(options);
      } catch (_error) {
        // Fallback to regular logout
        await logout(options);
      }
    },
    [logout]
  );

  /**
   * Direct cleanup without API call
   * Used as fallback when logout API fails or when no session exists
   */
  function performDirectCleanup(options?: LogoutOptions) {
    // Clear authentication context state
    clearState();

    // Clear storage using abstract method
    performAuthenticationCleanup(options?.completeCleanup ?? true);

    // Redirect using React Router
    const redirectPath = options?.redirectUrl || AUTHENTICATION_ROUTES.LOGIN;
    history.push(redirectPath);
  }

  return {
    /** Main logout function */
    logout,

    /** SSO-aware logout function (placeholder) */
    logoutWithSSO,

    /** Direct cleanup without API call */
    performDirectCleanup,

    /** Whether logout is in progress */
    isLoggingOut: closeSessionMutation.isLoading,

    /** Whether there's an active session to logout */
    hasActiveSession: !!(sessionId && systemId)
  };
}
